import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Wrench } from 'lucide-react';
import type { ExecutionDetail } from '@/components/copilot/mcp/chatTypes';

const NOISE_FIELDS = new Set([
  'expand',
  'self',
  'iconUrl',
  'avatarId',
  'entityId',
  'hierarchyLevel',
  'statusCategory',
  'avatarUrls',
  'icon',
  'subtask',
]);

const filterNoiseFields = (obj: unknown): unknown => {
  if (Array.isArray(obj)) {
    return obj.map(filterNoiseFields);
  }
  if (obj && typeof obj === 'object') {
    const filtered: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      if (NOISE_FIELDS.has(key)) continue;
      filtered[key] = filterNoiseFields(value);
    }
    return filtered;
  }
  return obj;
};

const formatToolName = (name: string): string =>
  name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

interface ExecutionDetailsProps {
  details: ExecutionDetail[];
}

export const ExecutionDetails: React.FC<ExecutionDetailsProps> = ({ details }) => {
  const [expanded, setExpanded] = useState(false);
  const [jsonExpanded, setJsonExpanded] = useState<Record<number, boolean>>({});

  if (!details || details.length === 0) return null;

  return (
    <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(prev => !prev)}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-500 hover:bg-gray-50 transition-colors"
      >
        {expanded ? (
          <ChevronDown className="w-3.5 h-3.5" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5" />
        )}
        <Wrench className="w-3.5 h-3.5" />
        <span>Execution Details ({details.length} tool{details.length > 1 ? 's' : ''})</span>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 divide-y divide-gray-100">
          {details.map((detail, idx) => (
            <div key={idx} className="px-3 py-2 space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-100 text-indigo-700">
                  {detail.tool_name}
                </span>
                <span className="text-xs text-gray-600">
                  {formatToolName(detail.action_name)}
                </span>
              </div>

              {detail.raw_response && (
                <div>
                  <button
                    onClick={() =>
                      setJsonExpanded(prev => ({ ...prev, [idx]: !prev[idx] }))
                    }
                    className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {jsonExpanded[idx] ? (
                      <ChevronDown className="w-3 h-3" />
                    ) : (
                      <ChevronRight className="w-3 h-3" />
                    )}
                    Raw Response
                  </button>
                  {jsonExpanded[idx] && (
                    <pre className="mt-1 p-2 bg-gray-50 rounded text-[11px] text-gray-700 overflow-x-auto max-h-64 overflow-y-auto font-mono leading-relaxed">
                      {JSON.stringify(filterNoiseFields(detail.raw_response), null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
